import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';

import { ItemsProvider, useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import CustomRequestProvider from '@app/components/CustomRequestProvider/CustomRequestProvider';
import CustomAvatar from '@app/components/UI/CustomAvatar/CustomAvatar';
import HelmetProvider from '@app/components/UI/HelmetProvider/HelmetProvider';
import LoadMoreButton from '@app/components/UI/LoadMoreButton/LoadMoreButton';
import MastheadNaked from '@app/components/UI/MastHead/MastheadNaked';
import MobileMenuSlideInMenu from '@app/components/UI/MobileSlideInMenu/MobileSlideInMenu';
import ModalVideo from '@app/components/UI/ModalVideo/ModalVideo';
import RichText from '@app/components/UI/RichText/RichText';
import SidebarWithCaptions from '@app/components/UI/SidebarWithCaptions/SidebarWithCaptions';
import { useIsMobile } from '@app/hooks/useIsMobile';
import imagePlaceholder from '@app/images/image-placeholder.png';
import { ContentEntity, ContentTopic, ContentType } from '@app/objects/ContentTopic';
import '@app/scss/components/blog.scss';

const ICON_LINK = [
	require('@app/images/arrow_circle.svg'),
	require('@app/images/text-button_link.svg'),
	require('@app/images/arrow_circle.svg'),
];

const TEXT_LINK = ['Read more', 'Visit the site', 'Watch Now'];

const initContentCount = 10;

const content = (
	<>
		<p>
			We love sharing our passion for veterinary hospice and in-home euthanasia, and our team would love to share their experience with you.
			Whether you are interested in euthanasia protocols, grief management, or hospice care tips, our Lap of Love veterinarians are
			available to speak at your veterinary hospital, club, organization, or meeting. Simply contact a Lap of Love veterinarian in your area
			to set up a speaking engagement.
		</p>
		<p>
			Lap of Love co-founders Drs. Mary Gardner and Dani McVety have presented across the country on a variety of topics related to
			geriatric care and euthanasia, and are also available to speak. As a speaker, author, consultant, and host for PetCare TV, Dr. Dani
			McVety enjoys empowering pet owners and veterinarians to seek the highest standards of pet care. Dr. Mary Gardner has presented at a
			number of major veterinary conferences, including the American Veterinary Medical Association (AVMA) Convention, North American
			Veterinary Conference (NAVC), Western Veterinary Conference (WVC), and International Association for Animal Hospice and Palliative
			Care (IAAHPC) Annual Conference, as well as numerous state and regional conferences and meetings. Both veterinarians are accomplished
			authors, speakers, and businesswomen who can tailor their presentations for professional and lay audiences. Please visit
			{' '}
			<a href="https://www.drdanimcvety.com/" target="_blank" rel="noopener noreferrer">
				Dr. McVety’s
			</a>
			and
			{' '}
			<a href="https://www.drmarygardner.com/" target="_blank" rel="noopener noreferrer">
				Dr. Gardner’s
			</a>
			{' '}
			personal web pages for more information about their upcoming speaking engagements.
		</p>
	</>
);

interface NewsContentProps {
	currentTopics: ContentTopic | undefined;
	searchQuery: string | undefined;
}

const NewsContent: React.FC<NewsContentProps> = ({ currentTopics, searchQuery }) => {
	const [modalVisible, setModalVisible] = React.useState<boolean>(false);
	const [videoSrc, setVideoSrc] = React.useState<string>('');
	const isMobile = useIsMobile();

	const context = useItemsProviderContext<ContentEntity>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { loading, items },
		actions: { handleChange },
	} = context;

	React.useEffect(() => {
		if (currentTopics) {
			handleChange({
				text: searchQuery || '',
				offset: 0,
			});
		}
	}, [currentTopics, searchQuery]);

	return (
		<>
			<div className="blog-page__content">
				{currentTopics && currentTopics.name === 'Speaking Engagements' && (
					<div className="blog-page__content--empty-text">
						<RichText text={content} />
					</div>
				)}
				{currentTopics && (
					<Helmet>
						<title>{`${currentTopics.name} | In The News | Lap of Love`}</title>
						<meta property="og:title" content={`${currentTopics.name} | In The News | Lap of Love`} />
						<meta name="twitter:title" content={`${currentTopics.name} | In The News | Lap of Love`} />
						<meta name="description" content={currentTopics.description || undefined} />
						<meta property="og:description" content={currentTopics.description || undefined} />
						<meta name="twitter:description" content={currentTopics.description || undefined} />
					</Helmet>
				)}

				{!loading && items.map((item, index) => (
					<article key={index} className="article">
						<div className="article__info">
							{isMobile && (
								<p className="article__name-date mobile">
									{item.source && <>
										{item.source}
										,&nbsp;
									</>}
									{new Intl.DateTimeFormat('en-US', {
										year: 'numeric',
										month: 'long',
										day: '2-digit',
										timeZone: 'UTC',
									}).format(item.date)}
								</p>
							)}
							<h6 className="article__caption">{item.title}</h6>
							<p className="article__text">{item.smallText}</p>
							<div className="article__addition">
								{item.contentType === ContentType.Video ? (
									<span
										onClick={() => {
											setModalVisible(true);
											setVideoSrc(item.url);
										}}
										className="article__more btn-text"
									>
										{TEXT_LINK[item.contentType]}
										{' '}
										<img className="arrow-icon" src={ICON_LINK[item.contentType]} alt="Arrow" />
									</span>
								) : item.contentType === ContentType.Article ? (
									<Link to={item.url} className="article__more btn-text" target="self">
										{TEXT_LINK[item.contentType]}
										{' '}
										<img className="arrow-icon" src={ICON_LINK[item.contentType]} alt="Arrow" />
									</Link>
								) : (
									<a href={item.url} className="article__more btn-text" target="_blank" rel="noopener noreferrer">
										{TEXT_LINK[item.contentType]}
										{' '}
										<img className="arrow-icon" src={ICON_LINK[item.contentType]} alt="Arrow" />
									</a>
								)}

								{!isMobile && (
									<span className="article__name-date">
										{item.source && (
											<>
												{item.source}
												,&nbsp;
											</>
										)}
										{item.date > 0 && (
											new Intl.DateTimeFormat('en-US', {
												year: 'numeric',
												month: 'long',
												day: '2-digit',
												timeZone: 'UTC',
											}).format(item.date)
										)}
									</span>
								)}
							</div>
						</div>
						<div className="article__photo">
							<CustomAvatar src={item.avatar} errorAvatar={imagePlaceholder} maxWidth={300} maxHeight={300} />
						</div>
					</article>
				))}

				{!loading && !items.length && currentTopics?.url !== 'speaking-engagements' && (
					<div className="faq-block__dummy">No issues found</div>
				)}

				{loading && <BounceLoader />}
				<div className="text-center">
					<LoadMoreButton
						className="btn btn-lg btn-secondary blog-page__load-more"
						visible={!loading}
					/>
				</div>
			</div>

			<ModalVideo modalVisible={modalVisible} setModalVisible={setModalVisible} videoSrc={videoSrc} />
		</>
	);
};

const NewsTopics: React.FC = () => {
	const context = useItemsProviderContext<ContentTopic>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { items },
		actions: { handleChange },
	} = context;

	const [currentTopics, setCurrentTopics] = React.useState<ContentTopic>();

	const [navCollapse, setNavCollapse] = React.useState(false);

	const [searchFieldValue, setSearchFieldValue] = React.useState('');
	const [searchQuery, setSearchQuery] = React.useState('');

	const params = useParams<{ id: string }>();

	const onSearchSubmit = (value) => {
		setSearchQuery(value);
		setSearchFieldValue(value);
	};

	React.useEffect(() => {
		handleChange();
	}, []);

	React.useEffect(() => {
		if (items.length) {
			if (params.id && items.length) {
				items.forEach((item) => {
					if (item.url === params.id) {
						setCurrentTopics(item);
						return false;
					}
				});
			}
		}
	}, [items]);

	React.useEffect(() => {
		window.scrollTo(0, 0);
		document.querySelector('#react-app')?.scrollTo(0, 0);
	}, []);

	return (
		<div className="blog-page">
			<MastheadNaked imageSrc={require('@app/images/blog-bg.jpg')} title={`${currentTopics ? currentTopics.name : 'In the News'}`} />
			<div className="blog-page-body">
				<MobileMenuSlideInMenu
					topics={items}
					onSetIssue={setCurrentTopics}
					typePage="NEWS_PAGE"
					onSubmit={onSearchSubmit}
					searchFieldValue={searchFieldValue}
					setSearchFieldValue={setSearchFieldValue}
				/>
				<div className="blog-page__sidebar">
					<SidebarWithCaptions
						onSetIssue={setCurrentTopics}
						navCollapse={navCollapse}
						topics={items}
						withSubmit={false}
						searchFieldName="searchFieldName"
						searchFieldPlaceHolder="Search by topic"
						typeTopicsInner="News"
						typePage="NEWS_PAGE"
						searchFieldValue={searchFieldValue}
						setSearchFieldValue={setSearchFieldValue}
						onSubmit={onSearchSubmit}
					/>
				</div>

				<ItemsProvider<ContentEntity>
					type={`contentTopics/news/${params.id ? `${params.id}/` : ''}content`}
					loadRequest={`contentTopics/news/${params.id ? `${params.id}/` : ''}content`}
					pagination={{ pageSize: initContentCount, total: 0 }}
				>
					<NewsContent currentTopics={currentTopics} searchQuery={searchQuery} />
				</ItemsProvider>
			</div>
		</div>
	);
};

const News: React.FC = () => {
	return (
		<CustomRequestProvider version="v2">
			<HelmetProvider ignoreServerPage>
				<ItemsProvider<ContentTopic>
					type="contentTopics/news"
					loadRequest="contentTopics/news"
				>
					<NewsTopics />
				</ItemsProvider>
			</HelmetProvider>
		</CustomRequestProvider>
	);
};

export default News;
